import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DEFAULT_ASSIGNMENT_LABELS, DEFAULT_ASSIGNMENT_KEYS } from "@/lib/defaultAssignment";
import { toast } from "sonner";

export function DefaultAssignmentsDialog({ projectId, onClose, onDone }) {
  const [rows, setRows] = useState(null);
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/projects/${projectId}/default-assignments`).then((r) => {
      setRows(r.data.items);
      setVals(Object.fromEntries(r.data.items.map((t) => [t.id, t.default_assignment || ""])));
    }).catch((e) => { toast.error(apiError(e)); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);
  const useSuggestions = () => setVals((v) => ({ ...v, ...Object.fromEntries(rows.filter((t) => !t.default_assignment && t.suggested).map((t) => [t.id, t.suggested])) }));
  const save = async () => {
    const items = rows.filter((t) => vals[t.id] && vals[t.id] !== t.default_assignment).map((t) => ({ task_id: t.id, default_assignment: vals[t.id] }));
    setSaving(true);
    try { const { data } = await api.post(`/projects/${projectId}/default-assignments`, { items }); toast.success(`${data.updated} task(s) updated`); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl" data-testid="default-assign-dialog">
        <DialogHeader><DialogTitle>Default Assignments</DialogTitle>
          <DialogDescription>Choose who receives each template task. Suggestions come from the task name and are only applied when you choose them and save.</DialogDescription></DialogHeader>
        {!rows ? <p className="text-sm text-slate-400">Loading…</p> : (
          <div className="max-h-[55vh] overflow-y-auto tr-scroll divide-y divide-slate-100 rounded-lg border border-slate-200">
            {rows.map((t) => (
              <div key={t.id} className="flex items-center gap-3 px-3 py-2" data-testid={`da-row-${t.id}`}>
                <p className="flex-1 min-w-0 text-sm text-slate-800 truncate">{t.title}
                  {!t.default_assignment && t.suggested && <span className="ml-2 text-[11px] text-slate-400">suggested: {DEFAULT_ASSIGNMENT_LABELS[t.suggested]}</span>}</p>
                <select className="h-8 rounded-md border border-slate-200 px-2 text-sm" value={vals[t.id] || ""} onChange={(e) => setVals({ ...vals, [t.id]: e.target.value })} data-testid={`da-select-${t.id}`}>
                  <option value="">Not set</option>
                  {DEFAULT_ASSIGNMENT_KEYS.map((k) => <option key={k} value={k}>{DEFAULT_ASSIGNMENT_LABELS[k]}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={useSuggestions} disabled={!rows} data-testid="da-use-suggestions">Fill in suggestions</Button>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-brand hover:bg-brand-strong" onClick={save} disabled={!rows || saving} data-testid="da-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
